/**
 * Dymki PrimeReact (`Tooltip`, także przez `CustomTooltip`) trzymają się w obrębie okna i przy celu.
 *
 * PrimeReact liczy dopasowanie do ekranu na dymku z wyzerowanym paddingiem i bez naszego marginesu
 * (`tooltip--top` itd.): przy celu blisko krawędzi dymek albo wystawał poza widok, albo — gdy jego
 * „fit” zadziałał — odjeżdżał daleko od celu. Po każdym ustawieniu pozycji (zmiana `style`) dymek
 * z zakotwiczonym celem (`anchorTooltip`, wołane z `CustomTooltip`) wraca na środek nad/pod celem,
 * a każdy dymek jest dosuwany do krawędzi okna z marginesem.
 */
const MARGIN = 8;

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

const anchors = new WeakMap<HTMLElement, { target: Element; position: TooltipPosition }>();

// Przesunięcie potrzebne, żeby dymek stał na środku nad/pod (lub obok) zakotwiczonego celu.
const centerOffset = (tooltip: HTMLElement, tip: DOMRect) => {
  const anchor = anchors.get(tooltip);
  if (!anchor || !anchor.target.isConnected) {
    return { dx: 0, dy: 0 };
  }
  const target = anchor.target.getBoundingClientRect();
  return anchor.position === 'top' || anchor.position === 'bottom'
    ? { dx: target.left + target.width / 2 - (tip.left + tip.width / 2), dy: 0 }
    : { dx: 0, dy: target.top + target.height / 2 - (tip.top + tip.height / 2) };
};

// Dodatkowe przesunięcie, żeby dymek (już po wyśrodkowaniu) zmieścił się w oknie z marginesem.
const clampOffset = (left: number, top: number, width: number, height: number) => {
  const viewportWidth = document.documentElement.clientWidth;
  const viewportHeight = document.documentElement.clientHeight;

  let dx = 0;
  if (left + width > viewportWidth - MARGIN) dx = viewportWidth - MARGIN - (left + width);
  if (left + dx < MARGIN) dx = MARGIN - left;

  let dy = 0;
  if (top + height > viewportHeight - MARGIN) dy = viewportHeight - MARGIN - (top + height);
  if (top + dy < MARGIN) dy = MARGIN - top;

  return { dx, dy };
};

// Docelowa pozycja liczona w całości (środek, potem dosunięcie do krawędzi) i zapisana JEDEN raz, tylko
// gdy się różni. Dwa osobne zapisy (środek odsuwał dymek za krawędź, dosunięcie go wracało) budziły
// obserwatora `style` w każdej klatce — przy celu blisko krawędzi dymek był przestawiany bez końca.
const adjust = (tooltip: HTMLElement) => {
  const tip = tooltip.getBoundingClientRect();
  if (!tip.width) {
    return;
  }
  const center = centerOffset(tooltip, tip);
  const clamp = clampOffset(tip.left + center.dx, tip.top + center.dy, tip.width, tip.height);
  const dx = center.dx + clamp.dx;
  const dy = center.dy + clamp.dy;
  if (Math.abs(dx) >= 0.5) tooltip.style.left = `${(parseFloat(tooltip.style.left) || 0) + dx}px`;
  if (Math.abs(dy) >= 0.5) tooltip.style.top = `${(parseFloat(tooltip.style.top) || 0) + dy}px`;
};

/**
 * Kotwiczy dymek przy jego celu — od teraz każda zmiana pozycji wraca na środek nad/pod celem.
 * W `onShow` PrimeReacta element dymka jeszcze nie istnieje (portal montuje się chwilę później), więc
 * dymek przychodzi jako funkcja, a kotwiczenie próbuje w kolejnych klatkach, aż się pojawi.
 */
export const anchorTooltip = (
  getTooltip: () => HTMLElement | null,
  target: Element | null,
  position: TooltipPosition,
  attemptsLeft = 10
) => {
  if (!target) {
    return;
  }
  const tooltip = getTooltip();
  if (!tooltip) {
    if (attemptsLeft > 0) {
      requestAnimationFrame(() => anchorTooltip(getTooltip, target, position, attemptsLeft - 1));
    }
    return;
  }
  anchors.set(tooltip, { target, position });
  adjust(tooltip);
};

const watchTooltip = (tooltip: HTMLElement) => {
  new MutationObserver(() => requestAnimationFrame(() => adjust(tooltip))).observe(tooltip, {
    attributes: true,
    attributeFilter: ['style'],
  });
  requestAnimationFrame(() => adjust(tooltip));
};

// Dymki są dopinane do `body` (domyślny `appendTo` PrimeReacta) — wystarczy obserwować jego dzieci.
new MutationObserver((mutations) => {
  mutations.forEach((mutation) =>
    mutation.addedNodes.forEach((node) => {
      if (node instanceof HTMLElement && node.getAttribute('role') === 'tooltip') {
        watchTooltip(node);
      }
    })
  );
}).observe(document.body, { childList: true });
